

















































































































































import Vue from 'vue';
import Component, { mixins } from 'vue-class-component';
import {
	mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import CreditCardSelector from '@/components/CreditCardSelector.vue';
import ReservationDetails from '@/components/ReservationDetails.vue';
import ReservationLocation from '@/components/ReservationLocation.vue';
import TermsAgreement from '@/components/TermsAgreement.vue';
import TransitionExpand from '@/components/TransitionExpand.vue';
import FormMixin from '@/mixins/formMixin';
import portalMixin from '@/mixins/portalMixin';
import ReservationStepMixin from '@/mixins/reservationStepMixin';
import CreditCard from '@/models/CreditCard';
import Customer from '@/models/Customer';
import Payment from '@/models/Payment';
import Quote from '@/models/Quote';
import Reservation from '@/models/Reservation';
import MarkdownText from '@/components/ui/MarkdownText.vue';

const Setup = Vue.extend({
	name: 'ReservationPayment',
	inject: ['$validator'],
	components: {
		CreditCardSelector,
		MarkdownText,
		ReservationDetails,
		ReservationLocation,
		TermsAgreement,
		TransitionExpand,
	},
	props: {
		active: Boolean,
		isEdit: Boolean,
	},
	computed: {
		...mapState(['isLoading', 'termsOfService']),
		...mapGetters(['customer']),
		...mapState('reservation', ['reservation']),
		...mapGetters('location', ['locationName', 'location']),
		...mapGetters('reservation', ['quote']),
	},
	methods: {
		...mapMutations('reservation', ['setPayment']),
		...mapActions('reservation', ['saveReservation']),
	},
});

@Component
export default class ReservationPayment extends mixins(Setup, FormMixin, portalMixin, ReservationStepMixin) {
	// Props
	public active!: boolean;
	public isEdit!: boolean;
	public refresh_happened: string = '';


	// Data
	public captcha_token: string = '';
	public payment: Payment = new Payment();
	public showAgreement = false;
	public validateCard = false;
	public refresh = true;

	// State
	public customer!: Customer;
	public isLoading!: boolean;
	public reservation!: Reservation;
	public termsOfService!: string;

	// Getters
	public location!: Locations.Location;
	public locationName!: string;
	public quote!: Quote;

	// Mutations
	public setPayment!: (payment: Payment) => void;

	// Actions
	public saveReservation!: () => Promise<any>;

	public get recaptchaKey(): boolean {
		const location = this.$store.getters['location/location'];
		return location.details.recaptcha_site_key;
	}

	public get requiresPayment(): boolean {
		return this.quote.is_prepay && this.quote.prepay_amount > 0;
	}

	public get settings(): Locations.Location {
		return this.$store.getters['location/settings'];
	}

	// Hooks
	public created() {
		if (!this.requiresPayment || this.isEdit) {
			this.setStep(this.Step.Summary);
		} else {
			this.payment.setReservationAmounts(this.reservation);
		}

		this.$nextTick(() => {
			if (this.recaptchaKey && !this.isEdit && this.settings.disable_hosted_cc_forms) {
				(window as any).grecaptcha.render('g-recaptcha');

				// hack to allow grecaptcha to see our callback
				(window as any).captcha_submit = (response: string) => { this.captcha_token = response; };
				(window as any).captcha_error = (_: any) => { this.captcha_token = ''; };
			}
		});
	}

	// Methods
	public async agreeToTerms() {
		console.log('Book Reservation Clicked...');
		if (!await this.$validator.validate()) {
			this.$store.dispatch('googleEvents/clckdSaveReservtionWthInvalidInfo', {
				fromInfo: '',
			});
			return;
		}

		if (this.termsOfService) {
			this.showAgreement = true;
			this.$store.dispatch('googleEvents/clckdBookReservationAndShowingTOS', {
				fromInfo: '',
			});
		} else {
			this.submit(true);
		}
	}

	public async save(card: CreditCard) {
		this.validateCard = false;

		if (!card || !card.token) {
			const errors = [
				{
					text: 'Credit Card is required for prepayment',
				},
			];

			this.$store.commit('setErrors', errors);

			// Reset reCaptcha
			if (this.recaptchaKey && !this.isEdit && this.settings.disable_hosted_cc_forms) {
				(window as any).grecaptcha.reset();
				this.captcha_token = '';
			}

			return;
		}

		console.log('Setting Payment...');
		this.payment.card = card;
		this.payment.type = 'card';
		this.setPayment(this.payment);
		this.$store.dispatch('googleEvents/clickedSaveReservation', {
			alsoCheckedSaveToAccountProfile: 'false',
			fromInfo: '',
		});
		console.log('Saving Reservation...');

		try {
			await this.saveReservation();
			this.$store.commit('clearErrors');
			this.setStep(this.Step.Confirmation);
			console.log('Reservation Saved...');
		} catch (errors) {
			// There was an error saving the reservation.
			const e = errors.errors;
			if (e.length > 0 && e[0].text.includes('already another reservation')) {
				this.$store.dispatch('googleEvents/duplicateReservationFound', {
					fromInfo: '',
				});
				this.$modal.show('DuplicateModal');
			} else {
				this.refresh = false;
				// Wait for the change to get flushed to the DOM
				this.$nextTick(() => {
					// Add the component back in
					this.refresh = true;
					this.refresh_happened = 'Card Data Invalid, Please Resubmit Card Details';
				});
			}
		}
	}

	public closeDuplicateReservationModel() {
		this.$store.dispatch('googleEvents/duplicateReservationFoundCanceled', {});
		this.$modal.hide('DuplicateModal');
	}

	public async submitDuplicate(agreed: boolean) {
		this.$store.dispatch('googleEvents/duplicateReservationAccepted', {});
		this.reservation.allow_dup = true;
		this.$modal.hide('DuplicateModal');
		this.submit(true);
	}

	public async submit(agreed: boolean) {
		this.validateCard = false;
		console.log('Agreed To Terms...');
		if (!agreed) {
			return;
		}

		if (!await this.$validator.validateAll()) {
			return;
		}

		if (this.recaptchaKey && !this.captcha_token && this.settings.disable_hosted_cc_forms) {
			const errors = [
				{
					text: 'Please complete the captcha challenge.',
				},
			];

			this.$store.commit('setErrors', errors);
			return;
		}

		this.validateCard = true;
	}

	public uxAction() {
		this.$store.dispatch('analytics/action', {
			type: 'UX',
			label: 'click',
			value: 'View Terms and Make Payment',
			step: this.$store.state.reservation.step,
		} as Analytics.ActionParams);
	}
}
